import { FC, PropsWithChildren, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { FloatingPortal } from '@floating-ui/react';
import { Placement } from '@floating-ui/react';
import composeRefs from '@seznam/compose-react-refs';
import { CalloutBanner } from '@frontend/high-value-adoption';
import { useTranslation } from '@frontend/i18n';
import { Button, PopoverDialog, Text, usePopoverDialog } from '@frontend/design-system';
import { useAnchorVisibility } from '../../../../hooks/use-anchor-visibility';

type BaseCalloutProps = {
  anchorRef: React.RefObject<HTMLElement>;
  innerRef?: React.Ref<HTMLDivElement>;
  onDismiss?: () => void;
  showBanner: boolean;
  placement: Placement;
  arrowPosition: 'top' | 'bottom' | 'left' | 'right';
};

export const BaseCallout: FC<PropsWithChildren<BaseCalloutProps>> = ({
  anchorRef,
  innerRef,
  onDismiss,
  showBanner,
  placement,
  arrowPosition,
}) => {
  const { t } = useTranslation('highValueAdoption');
  const ref = useRef<HTMLDivElement>(null);
  const isAnchorVisible = useAnchorVisibility(anchorRef);
  const [isReferenceSet, setIsReferenceSet] = useState(false);

  const { getDialogProps, open, close, refs } = usePopoverDialog({
    placement,
  });

  // Set up the floating reference when anchor element becomes available
  useEffect(() => {
    let checkInterval: NodeJS.Timeout | null = null;

    const setupReference = (element: HTMLElement) => {
      refs.setReference(element);
      setIsReferenceSet(true);
      if (checkInterval) {
        clearInterval(checkInterval);
        checkInterval = null;
      }
    };

    // If element is available immediately, set it up
    if (anchorRef.current && !isReferenceSet) {
      setupReference(anchorRef.current);
    } else if (!anchorRef.current) {
      // Otherwise, poll until element becomes available
      checkInterval = setInterval(() => {
        if (anchorRef.current) {
          setupReference(anchorRef.current);
        }
      }, 100);
    }

    return () => {
      if (checkInterval) {
        clearInterval(checkInterval);
      }
    };
  }, [anchorRef, refs, isReferenceSet]);

  // Open/close based on showBanner, anchor visibility, and reference being set
  useEffect(() => {
    if (showBanner && isAnchorVisible && isReferenceSet) {
      open();
    } else {
      close();
    }
  }, [showBanner, isAnchorVisible, isReferenceSet, open, close]);

  const handleDismiss = () => {
    onDismiss?.();
    close();
  };

  if (!showBanner) {
    return null;
  }

  return (
    <FloatingPortal root={document.getElementById('root')}>
      <PopoverDialog {...getDialogProps()} css={styles.dialog}>
        <CalloutBanner
          arrowPosition={arrowPosition}
          badge='new'
          innerRef={composeRefs(innerRef, ref) as React.Ref<HTMLDivElement>}
          title={
            <Text size='medium' weight='bold'>
              {t('Bulk Collections')}
            </Text>
          }
          description={<Text size='medium'>{t('Send payment requests to multiple customers at once.')}</Text>}
          actions={
            <Button variant='tertiary' size='small' onClick={handleDismiss} css={styles.actionBtn}>
              {t('Dismiss')}
            </Button>
          }
          containerStyles={css`
            .callout-banner-content {
              box-shadow: var(--fabric-tooltip-shadow);
              margin: var(--fabric-space-padding-md);
            }
          `}
        />
      </PopoverDialog>
    </FloatingPortal>
  );
};

const styles = {
  dialog: css`
    width: 336px;
    padding: 0;
    box-shadow: none;
    z-index: 30;
    background: transparent;
  `,
  actionBtn: css`
    color: var(--fabric-color-neutral-alpha-100-alpha-60);
    font-weight: var(--fabric-font-weight-regular);
    :hover {
      color: var(--fabric-color-seaweed-60);
      background: transparent;
      font-weight: 500;
      cursor: pointer;
    }
  `,
};
